import type { FC, HTMLAttributes } from 'react';
import { memo } from 'react';
import { FormattedDate, useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { BlockedContent } from '../../../../graphdebate/src/components/Blocking/BlockedContent';
import { BlockUserOption } from '../../../../graphdebate/src/components/UserActions/BlockUserOption';
import { Tooltip } from '../../components/GraphModal/Tooltip';
import { ModerationUserVisibilityOnlyInfos } from '../../components/Moderation/ModerationInfos';
import { dsaClassHandler } from '../../contributor/Profile/DSA/dsaClassHandler';
import { DsaMessage } from '../../contributor/Profile/DSA/DsaMessage';
import { useWebsite } from '../../hooks/useWebsite';
import type { Topic, User } from '../../types/api';
import { getLocaleDateDigits } from '../../utils/date';
import type { DensityType } from '../../utils/density';
import { AuthorPseudo } from '../AuthorPseudo';
import { TopicItemInteractions } from './children/v3/TopicItemInteractions';
import { TopicItemLastMessageBlock } from './children/v3/TopicItemLastMessageBlock';
import TopicItemTags from './children/v3/TopicItemTags';
import TopicItemTitle from './children/v3/TopicItemTitle';
import { TopicRemoveButton } from './Remove/TopicRemoveButton';
import { TopicListItemActions } from './TopicListItemActions';

interface TopicListItemV3Props extends HTMLAttributes<HTMLDivElement> {
  topic: Topic;
  density?: DensityType;
  withRemove?: boolean;
  withActions?: boolean;
  isResponsive?: boolean;
  isSidebar?: boolean;
  itemTitleClassName?: string;
  shouldHandleDsaDisplay?: boolean;
  isAuthorBlocked?: boolean;
}

export const TopicListItemV3: FC<TopicListItemV3Props> = memo(function TopicListItemV3(props) {
  const {
    topic,
    density,
    withRemove,
    withActions,
    className,
    isResponsive = true,
    isSidebar = false,
    itemTitleClassName = '',
    shouldHandleDsaDisplay = false,
    isAuthorBlocked = false,
    ...otherProps
  } = props;
  const { graphdebate } = useWebsite();
  // @ts-ignore
  const shouldIndexDiscussion = graphdebate?.meta?.topic?.index ?? 0;

  const { locale } = useIntl();

  const displayedDate = getLocaleDateDigits(topic.createdAt, locale);

  const isCompactDensity = density === 'compact';

  return (
    <div
      className={twMerge(
        'TopicListItemV3 p-3 flex text-gray-darkv4',
        isResponsive && 'md:p5',
        isSidebar && 'px-0',
        shouldHandleDsaDisplay && [dsaClassHandler(topic.status), 'my-4'],
        className,
      )}
      {...otherProps}
    >
      <div className="infos flex-1 overflow-hidden">
        <div className="head flex">
          <div
            className={twMerge(
              'post-infos flex gap-x-1 text-[13px] items-end',
              isSidebar && 'md:text-xs',
              isResponsive && 'md:text-[15px]',
            )}
          >
            <AuthorPseudo
              author={topic.author}
              userId={(topic.author as User).id}
              profile={(topic.author as User).profile}
              withProfilePopover
              containerClassName={'inline truncate'}
              pseudoClassName={twMerge(
                'author font-medium cursor-pointer truncate hover:text-primary',
                isResponsive && 'md:text-[15px] md:font-semibold',
                isSidebar && 'md:text-xs',
              )}
            />

            <div
              className={twMerge(
                'date text-gray-soft',
                isSidebar && 'md:text-xs md:text-gray-400 md:font-medium',
              )}
            >
              <Tooltip
                content={
                  <FormattedDate
                    value={new Date(topic.createdAt)}
                    year="numeric"
                    month="long"
                    day="numeric"
                    weekday="long"
                    hour="numeric"
                    minute="numeric"
                  />
                }
                touch={true}
                touchTimeout={1500}
              >
                <span>{displayedDate}</span>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Native blocking: replace topic content with blocked notice */}
        {isAuthorBlocked ? (
          <BlockedContent className="mt-2" />
        ) : (
          <>
            <TopicItemTitle
              topic={topic}
              isResponsive={isResponsive}
              itemTitleClassName={itemTitleClassName}
              className="font-medium"
            />

            <TopicItemInteractions
              topic={topic}
              isResponsive={isResponsive}
              isSidebar={isSidebar}
            />
            {!isCompactDensity && <TopicItemTags topic={topic} isResponsive={isResponsive} />}
          </>
        )}

        <DsaMessage
          contentType="topic"
          contentStatus={topic.status}
          moderationMotifId={topic.moderationMotifId}
          className={twMerge(
            'absolute top-0 right-4 transform -translate-y-1/3 px-3 rounded-full text-white text-sm',
            topic.status === 'pending' ? 'bg-gray-light' : 'bg-primary',
          )}
        />
      </div>

      {shouldHandleDsaDisplay && topic.status !== 'approved' && (
        <ModerationUserVisibilityOnlyInfos
          contentType="message"
          className="absolute bottom-0 right-2 text-gray-medium text-sm"
        />
      )}

      {!isAuthorBlocked && (
        <TopicItemLastMessageBlock
          topic={topic}
          className={twMerge(isResponsive ? '_md:hidden' : 'hidden')}
          isResponsive={isResponsive}
          isSidebar={isSidebar}
        />
      )}

      {(withRemove || withActions) && (
        <div className="flex flex-col justify-between items-center ml-auto">
          {withRemove && (
            <div className="my-4">
              <TopicRemoveButton topicId={topic.id as string} content={topic.content as string} />
            </div>
          )}

          {withActions && (
            <>
              <TopicListItemActions
                topic={topic}
                className="ml-auto"
                isAuthorBlocked={isAuthorBlocked}
              />
              <BlockUserOption
                userId={(topic.author as User)?.id}
                pseudo={(topic.author as User)?.profile?.pseudo || topic.author?.username}
                picture={(topic.author as User)?.profile?.picture}
                className="mt-2"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
});
